import { Injectable, OnDestroy } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterState,
} from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { ConsentService } from './consent.service';

// The Contentsquare UXA snippet bootstraps a `_uxa` command queue on
// window and replaces it with the real implementation once it loads.
// Until then, `_uxa.push([...])` calls are buffered and drained on load.
// Ambient augmentation kept inside this module (rather than a top-level
// .d.ts) so the betterer-driven tsc program — built only from the .ts
// files in its include glob — still sees it.
declare global {
  interface Window {
    _uxa?: unknown[][];
  }
}

// Cookie the UXA tag reads to suppress all collection. Pushing `['optout']`
// writes it; deleting it is the (undocumented but only) way back in.
const OPTOUT_COOKIE = '_cs_optout';

@Injectable({ providedIn: 'root' })
export class ContentsquareService implements OnDestroy {
  // The tag is installed exactly once per app lifetime. Guards a second
  // init() against re-injecting the script or double-subscribing.
  private injected = false;
  private consentSub: Subscription | null = null;
  private routerSub: Subscription | null = null;

  constructor(
    private router: Router,
    private consent: ConsentService
  ) {}

  ngOnDestroy(): void {
    // Safe today (providedIn: 'root', never destroyed in practice); kept as
    // defensive insurance if this is ever scoped to a feature module, where
    // the router/consent subscriptions would otherwise leak.
    this.consentSub?.unsubscribe();
    this.consentSub = null;
    this.routerSub?.unsubscribe();
    this.routerSub = null;
  }

  /**
   * Install the Contentsquare UXA tag once, following the vendor's
   * documented opt-out model: the tag is always loaded, and a user's
   * refusal is signalled with `['optout']` (which writes the `_cs_optout`
   * cookie and clears the `_cs_*` cookies). There is no documented opt-in
   * command — re-enabling is done by deleting that cookie.
   *
   * Booting opted-out by default keeps the tag detectable for installation
   * verification while collecting nothing until the user opts in. This
   * trades a stricter "no third-party script before consent" posture for
   * the vendor's standard, self-verifiable install — a DPO-acknowledged
   * decision recorded in tech-matrix repositories/rh/web-b2b.md.
   */
  init(): void {
    const projectId = environment.contentsquareProjectId;
    if (!projectId) return; // no-op in dev/staging/prod where it is null
    if (this.injected) return;
    this.injected = true;

    // Seed the queue, then let consent drive the opt state. category$ emits
    // the current decision synchronously on subscribe, so the boot opt state
    // is applied (optout queued / cookie cleared) BEFORE the async snippet
    // executes and drains the queue.
    window._uxa = window._uxa || [];
    this.consentSub = this.consent
      .category$('sessionRecording')
      .subscribe((granted) => this.applyConsent(granted));

    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => this.trackPageview(event.urlAfterRedirects));

    this.injectSnippet(projectId);
  }

  private applyConsent(granted: boolean): void {
    if (granted) {
      // Re-enable collection. With no documented opt-in command, the inverse
      // of optout is deleting its cookie. Immediate on a fresh boot; on a
      // mid-session grant the SDK resumes from the next navigation — we
      // deliberately avoid a disruptive page reload.
      this.clearOptoutCookie();
      return;
    }
    // Refuse collection. The queued optout stops data being sent without
    // removing the tag or reloading; we also clear `_cs_*` ourselves so the
    // revocation holds even if the SDK never loaded (CSP block, ad blocker,
    // or the project's snippet endpoint not yet serving real JS).
    window._uxa?.push(['optout']);
    this.clearContentsquareCookies();
  }

  private injectSnippet(projectId: string): void {
    const script = document.createElement('script');
    script.async = true;
    // Single URL per projectId; versioning is server-side (no `?sv=` like
    // Hotjar had). Documented as debt in tech-matrix
    // repositories/rh/web-b2b.md.
    script.src = `https://t.contentsquare.net/uxa/${projectId}.js`;
    document.head.appendChild(script);
  }

  private trackPageview(url: string): void {
    // Only label pageviews for opted-in users on the in-scope routes. An
    // opted-out session sends nothing regardless; skipping avoids buffering
    // noise. Note: once loaded the SDK records the whole session — route
    // gating scopes the named pageviews, not the recording itself.
    if (!this.consent.has('sessionRecording')) return;
    if (!this.isTrackedRoute(this.router.routerState)) return;
    // Contentsquare drops the '#' fragment and caps the path at 255 chars;
    // normalise here so the buffered value matches what the SDK would store.
    const path = (url.split('#')[0] ?? '').slice(0, 255);
    window._uxa?.push(['trackPageview', path]);
  }

  private isTrackedRoute(state: RouterState): boolean {
    const visit = (route: ActivatedRoute): boolean => {
      if (route.snapshot.data?.['tracking'] === true) return true;
      return route.children.some(visit);
    };
    return visit(state.root);
  }

  private clearContentsquareCookies(): void {
    document.cookie.split(';').forEach((entry) => {
      const name = entry.split('=')[0]?.trim();
      if (name && name.startsWith('_cs_')) this.expireCookie(name);
    });
  }

  private clearOptoutCookie(): void {
    this.expireCookie(OPTOUT_COOKIE);
  }

  private expireCookie(name: string): void {
    // Clear on the current host and one level up (the registrable domain for
    // typical 2-label hosts such as `app.closecare.com.br` →
    // `.closecare.com.br`), plus without a domain attribute for cookies set
    // on the exact current origin.
    const domains = this.cookieDomainCandidates(document.location.hostname);
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
    });
  }

  private cookieDomainCandidates(host: string): string[] {
    const parts = host.split('.');
    const candidates = ['.' + host];
    if (parts.length > 1) {
      candidates.push('.' + parts.slice(1).join('.'));
    }
    return candidates;
  }
}
